import { MouseEvent, useRef } from "react";
import { Box } from "@mui/material";
import { DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import MentionInput from "@common/components/mentionInput/MentionInput.tsx";
import PolicyRichTextView from "./PolicyRichTextView";
import PolicyVersionBadge from "../common/PolicyVersionBadge";
import { usePolicySectionEditorStyles } from "./PolicySectionsEditor.styles.ts";

export interface PolicyVersionDetailsProps {
    title: string | null;
    description: string | null;
    currentVersion: string | undefined;
    isEditable: boolean;
    isInitialDraft: boolean;
    onTitleChange: (title: string) => void;
    onDescriptionChange: (description: string) => void;
    isEditingContent: boolean;
    onStartEditingContent: () => void;
}

function PolicyVersionDetails(props: PolicyVersionDetailsProps) {
    const { translateString } = useTranslation();
    const styles = usePolicySectionEditorStyles();
    const { title, description, currentVersion, isEditable, isInitialDraft, onTitleChange, onDescriptionChange, isEditingContent, onStartEditingContent } =
        props;
    const caretCoordsRef = useRef<{ x: number; y: number } | null>(null);

    const handleStartEditingDescription = (event: MouseEvent<HTMLDivElement>) => {
        // Switch on mousedown (see PolicyEditableItem): blurring the active editor shifts layout between
        // mousedown and mouseup, so a plain click can miss. Flush the active editor's save first.
        const rect = event.currentTarget.getBoundingClientRect();
        caretCoordsRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
        event.preventDefault();
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        onStartEditingContent();
    };

    const hasDescription = !!description && description.trim().length > 0;

    return (
        <Box marginBottom={2}>
            <Box display="flex" alignItems="center" gap={1}>
                <Box
                    flexGrow={1}
                    sx={(theme) => ({
                        "& .MuiInputBase-root": { fontWeight: theme.typography.fontWeightBold },
                        "& .MuiInputBase-root .MuiInputBase-input": {
                            fontSize: theme.typography.pxToRem(30),
                        },
                    })}
                >
                    <DotLegalTextField
                        variant="standard"
                        label=""
                        value={title}
                        onChange={onTitleChange}
                        debounce
                        disabled={!isEditable}
                        maxLength={2400}
                        placeholder={translateString("title")}
                    />
                </Box>
                {currentVersion && <PolicyVersionBadge version={currentVersion} isDraft={isEditable} isInitialDraft={isInitialDraft} />}
            </Box>
            <Box
                sx={(theme) => ({
                    "& .MuiInputBase-input": {
                        fontSize: theme.typography.pxToRem(14),
                    },
                })}
            >
                {!isEditable && hasDescription && <PolicyRichTextView html={description ?? ""} />}
                {isEditable && isEditingContent && (
                    <MentionInput
                        disableMentions
                        multiline
                        debounce
                        variant={"standard"}
                        noMargin
                        value={description ?? ""}
                        placeholder={translateString("description")}
                        onChange={onDescriptionChange}
                        focusCaretOffset={caretCoordsRef.current}
                        disableScroll
                    />
                )}
                {isEditable && !isEditingContent && (
                    <Box onMouseDown={handleStartEditingDescription} onClick={(event) => event.preventDefault()} sx={{ cursor: "text", minHeight: "1.5em" }}>
                        {hasDescription ? (
                            <PolicyRichTextView html={description ?? ""} />
                        ) : (
                            <Box component="span" sx={styles.inlinePlaceholder}>
                                {translateString("description")}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default PolicyVersionDetails;
